/**
 * Freshness judged on the DATA frontier, not on sync success.
 *
 * A sync job can finish "success" while the data stands still (Google sat at
 * 08-24 through a week of green runs), so "synced 5 นาทีก่อน" answers the
 * wrong question. What the reader needs is "ข้อมูลถึงวันไหน" — per platform,
 * on the Bangkok calendar — and one strip that totals it up.
 *
 * Pure date arithmetic on YYYY-MM-DD strings; no Date-now reads in here so
 * the caller controls "today" (and tests are deterministic).
 */
import type { PlatformAccount } from "@/types";

/** Frontier within this many days of today still counts as fresh — platforms
 * finalise yesterday's numbers overnight, so a 1-day lag is normal delivery. */
const FRESH_LAG_DAYS = 1;

/** "none" = the platform genuinely delivered nothing; "unknown" = we could
 *  not find out. Collapsing the two told the reader an account was dormant
 *  when in fact a query had failed. */
export type FrontierTone = "fresh" | "dormant" | "none" | "unknown";

export interface FrontierFacts {
  tone: FrontierTone;
  /** Whole days between the frontier and today; null when there is no data. */
  staleDays: number | null;
}

export const daysBetween = (fromIso: string, toIso: string): number => {
  const from = Date.parse(`${fromIso}T00:00:00Z`);
  const to = Date.parse(`${toIso}T00:00:00Z`);
  return Math.round((to - from) / 86_400_000);
};

export const frontierFacts = (
  frontier: string | null | undefined,
  todayIso: string,
  /** true when the lookup for this platform failed, so absence proves nothing. */
  unreadable = false,
): FrontierFacts => {
  if (unreadable) return { tone: "unknown", staleDays: null };
  if (!frontier) return { tone: "none", staleDays: null };
  const staleDays = daysBetween(frontier, todayIso);
  return { tone: staleDays <= FRESH_LAG_DAYS ? "fresh" : "dormant", staleDays };
};

export interface DormantEntry {
  platform: string;
  /** Last date with data, or null when the platform has never delivered. */
  since: string | null;
  staleDays: number | null;
}

export interface ConnectionSummary {
  freshCount: number;
  dormant: DormantEntry[];
  /** Platforms whose frontier could not be read — neither fresh nor dormant. */
  unknown: string[];
  /** Accounts whose credential is expired / in error — a different problem
   * from dormant delivery, and the strip must not conflate the two. */
  tokenIssues: number;
  total: number;
}

const TOKEN_ISSUE_STATUSES = new Set(["expired", "error"]);

export const summarizeConnections = (
  accounts: Pick<PlatformAccount, "platform" | "status">[],
  frontiers: Map<string, string>,
  todayIso: string,
  /** Platforms whose frontier lookup failed — absence proves nothing for them. */
  unreadable: ReadonlySet<string> = new Set(),
): ConnectionSummary => {
  const platforms = [...new Set(accounts.map((a) => a.platform))];
  const dormant: DormantEntry[] = [];
  const unknown: string[] = [];
  let freshCount = 0;
  for (const platform of platforms) {
    const facts = frontierFacts(frontiers.get(platform) ?? null, todayIso, unreadable.has(platform));
    if (facts.tone === "unknown") unknown.push(platform);
    else if (facts.tone === "fresh") freshCount += 1;
    else dormant.push({ platform, since: frontiers.get(platform) ?? null, staleDays: facts.staleDays });
  }
  return {
    freshCount,
    dormant,
    unknown,
    tokenIssues: accounts.filter((a) => TOKEN_ISSUE_STATUSES.has(a.status)).length,
    total: accounts.length,
  };
};
